import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Sidebar.css';

const Sidebar = () => {
  const location = useLocation();
  const sidebarRef = useRef(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSubmenu, setExpandedSubmenu] = useState(null);

  // Navigation sections data
  const navigationSections = [
    {
      title: 'Main',
      items: [
        { 
          path: '/dashboard', 
          label: 'Dashboard', 
          icon: 'fas fa-tachometer-alt',
          isActive: true
        },
        { 
          path: '/clients', 
          label: 'Clients', 
          icon: 'fas fa-building',
          badge: { count: 3, type: 'info' }
        },
        { 
          path: '/projects', 
          label: 'Projects', 
          icon: 'fas fa-project-diagram',
          badge: { count: 3, type: 'primary' }
        },
        { 
          path: '/tasks', 
          label: 'Tasks', 
          icon: 'fas fa-tasks',
          badge: { count: 8, type: 'warning' }
        },        
        // { 
        //   path: '/calendar', 
        //   label: 'Calendar', 
        //   icon: 'fas fa-calendar-alt'
        // }
      ]
    },
    {
      title: 'Collaboration',
      items: [
        { 
          path: '/people', 
          label: 'People', 
          icon: 'fas fa-users'
        },
        { 
          path: '/messages', 
          label: 'Messages', 
          icon: 'fas fa-comments',
          badge: { count: 2, type: 'danger' }
        },
        { 
          path: '/shared-files', 
          label: 'Shared Files', 
          icon: 'fas fa-share-alt'
        }
      ]
    },
    {
      title: 'Analytics',
      items: [
        { 
          path: '/reports', 
          label: 'Reports', 
          icon: 'fas fa-chart-bar',
          hasSubmenu: true,
          submenu: [
            { path: '/reports/overview', label: 'Overview' },
            { path: '/reports/projects', label: 'Project Reports' },
            { path: '/reports/team', label: 'Team Reports' }
          ]
        },
        { 
          path: '/analytics', 
          label: 'Analytics', 
          icon: 'fas fa-chart-line'
        }
      ]
    },
    {
      title: 'QC Management',
      items: [
        { 
          path: '/qc/overview', 
          label: 'QC Management', 
          icon: 'fas fa-vial',
          badge: { count: 8, type: 'warning' }
        }
      ]
    },
    {
      title: 'Settings',
      items: [
        { 
          path: '/settings', 
          label: 'Settings', 
          icon: 'fas fa-cog'
        },
        { 
          path: '/help', 
          label: 'Help & Support', 
          icon: 'fas fa-question-circle'
        }
      ]
    }
  ];

  // Handle responsive layout
  useEffect(() => {
    const handleResize = () => {
      const prevIsMobile = isMobile;
      setIsMobile(window.innerWidth <= 768);
      
      if (prevIsMobile !== (window.innerWidth <= 768)) {
        if (window.innerWidth <= 768) {
          setIsCollapsed(false);
          setIsOpen(false);
        } else {
          const wasCollapsed = localStorage.getItem('sidebarCollapsed') === 'true';
          setIsCollapsed(wasCollapsed);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  // Define toggleCollapse with useCallback to stabilize it
  const toggleCollapse = useCallback(() => {
    if (!isMobile) {
      setIsCollapsed(prevCollapsed => {
        const newCollapsed = !prevCollapsed;
        localStorage.setItem('sidebarCollapsed', newCollapsed.toString());
        
        if (newCollapsed) {
          setExpandedSubmenu(null);
        }
        
        return newCollapsed;
      });
    }
  }, [isMobile]);

  // Handle sidebar toggle from header
  useEffect(() => {
    const handleToggleSidebar = () => {
      if (isMobile) {
        setIsOpen(prevIsOpen => !prevIsOpen);
      } else {
        toggleCollapse();
      }
    };

    document.addEventListener('toggleSidebar', handleToggleSidebar);
    return () => document.removeEventListener('toggleSidebar', handleToggleSidebar);
  }, [isMobile, toggleCollapse]);

  // Dispatch sidebar state changes to header for mobile menu icon sync
  useEffect(() => {
    if (isMobile) {
      document.dispatchEvent(new CustomEvent('sidebarStateChanged', { 
        detail: { isOpen } 
      }));
    }
  }, [isMobile, isOpen]);

  // Close mobile sidebar on escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isMobile && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMobile, isOpen]);

  // Load collapsed state from localStorage
  useEffect(() => {
    if (!isMobile) {
      const savedCollapsed = localStorage.getItem('sidebarCollapsed') === 'true';
      setIsCollapsed(savedCollapsed);
    }
  }, [isMobile]);

  // Close mobile sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobile && isOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile, isOpen]);

  const closeMobileSidebar = () => {
    if (isMobile) {
      setIsOpen(false);
    }
  };

  const toggleSubmenu = (itemPath) => {
    if (isCollapsed) return;
    
    setExpandedSubmenu(expandedSubmenu === itemPath ? null : itemPath);
  };

  const isActiveLink = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const getBadgeClass = (type) => {
    const badgeClasses = {
      primary: 'sidebar-link-badge badge-primary',
      success: 'sidebar-link-badge badge-success',
      warning: 'sidebar-link-badge badge-warning',
      danger: 'sidebar-link-badge badge-danger',
      info: 'sidebar-link-badge badge-info',
      secondary: 'sidebar-link-badge badge-secondary'
    };
    return badgeClasses[type] || 'sidebar-link-badge badge-secondary';
  };

  const renderNavItem = (item, sectionIndex, itemIndex) => {
    const isActive = isActiveLink(item.path);
    const hasSubmenu = item.hasSubmenu && item.submenu;
    const isSubmenuExpanded = expandedSubmenu === item.path;

    const NavElement = item.path.startsWith('/') ? Link : 'button';
    const navProps = item.path.startsWith('/') 
      ? { to: item.path }
      : { 
          onClick: () => hasSubmenu && toggleSubmenu(item.path),
          type: 'button'
        };

    return (
      <li key={`${sectionIndex}-${itemIndex}`} className="sidebar-item">
        <NavElement
          {...navProps}
          className={`sidebar-link ${isActive ? 'active' : ''}`}
          aria-current={isActive ? 'page' : undefined}
          onClick={(e) => {
            if (hasSubmenu) {
              e.preventDefault();
              toggleSubmenu(item.path);
            } else if (isMobile) {
              closeMobileSidebar();
            }
          }}
        >
          <div className="sidebar-link-icon">
            <i className={item.icon}></i>
          </div>
          <span className="sidebar-link-text">{item.label}</span>
          {item.badge && (
            <span className={getBadgeClass(item.badge.type)}>
              {item.badge.count}
            </span>
          )}
          {hasSubmenu && (
            <i className={`fas fa-chevron-right sidebar-link-arrow ${isSubmenuExpanded ? 'expanded' : ''}`}></i>
          )}
        </NavElement>
        
        {hasSubmenu && (
          <ul className={`submenu ${isSubmenuExpanded ? 'show' : ''}`}>
            {item.submenu.map((subItem, subIndex) => {
              const isSubActive = isActiveLink(subItem.path);
              return (
                <li key={subIndex} className="sidebar-item">
                  <Link
                    to={subItem.path}
                    className={`sidebar-link ${isSubActive ? 'active' : ''}`}
                    aria-current={isSubActive ? 'page' : undefined}
                    onClick={() => isMobile && closeMobileSidebar()}
                  >
                    <span className="sidebar-link-text">{subItem.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </li>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobile && isOpen && (
        <div className="sidebar-overlay show" onClick={closeMobileSidebar}></div>
      )}
      
      {/* Sidebar */}
      <aside 
        ref={sidebarRef}
        className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobile && isOpen ? 'show' : ''}`}
      >
        <nav className="sidebar-content" aria-label="Main navigation">
          {navigationSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="sidebar-section">
              <div className="sidebar-section-title">{section.title}</div>
              <ul className="sidebar-nav">
                {section.items.map((item, itemIndex) => 
                  renderNavItem(item, sectionIndex, itemIndex)
                )}
              </ul>
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
